import { LogOut, Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'
import { useLocation } from 'react-router-dom'
import { useTopBar } from '@/contexts/TopBarContext'

interface TopBarProps {
    onMobileMenuClick?: () => void
}

const navItems = [
    { label: 'Competitor', path: '/app/competitor' },
    { label: 'Availability', path: '/app/availability' },
    { label: 'Visibility', path: '/app/visibility' },
    { label: 'Performance', path: '/app/performance' },
    { label: 'Store Maps', path: '/app/store-maps' },
    { label: 'Settings', path: '/app/settings', matchPrefix: true },
]

export function TopBar({ onMobileMenuClick }: TopBarProps) {
    const { user, signOut } = useAuth()
    const location = useLocation()
    const { filterContent } = useTopBar()

    const currentModule = navItems.find((item) =>
        item.matchPrefix
            ? location.pathname.startsWith(item.path)
            : location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    )?.label || ''

    return (
        <header className="h-14 flex items-center justify-between px-4 lg:px-6 z-50 shrink-0 relative bg-white/40 dark:bg-black/30 backdrop-blur-2xl backdrop-saturate-150 border-b border-white/30 dark:border-white/10 shadow-[0_4px_16px_rgba(0,0,0,0.04)]">
            {/* Left: Hamburger (mobile) + Module name */}
            <div className="flex items-center gap-2 flex-shrink-0">
                {onMobileMenuClick && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onMobileMenuClick}
                        className="lg:hidden h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-white/30 dark:hover:bg-white/10 rounded-lg"
                    >
                        <Menu className="h-5 w-5" />
                    </Button>
                )}
                <h2 className="text-sm font-bold text-foreground tracking-tight">{currentModule}</h2>
            </div>

            {/* Center: Filter content slot (only rendered when a page sets it) */}
            {filterContent && (
                <div className="flex-1 flex items-center justify-center mx-4 min-w-0">
                    {filterContent}
                </div>
            )}

            {/* Right: User avatar only */}
            <div className="flex items-center gap-3 flex-shrink-0">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0 hover:bg-white/30 dark:hover:bg-white/10">
                            <Avatar className="h-8 w-8 border border-white/40 shadow-sm">
                                <AvatarImage src={user?.user_metadata?.avatar_url} alt={user?.email || ''} />
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                                    {user?.email?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="end" forceMount>
                        <DropdownMenuLabel className="font-normal">
                            <div className="flex flex-col space-y-1">
                                <p className="text-sm font-medium leading-none">
                                    {user?.user_metadata?.full_name || 'User'}
                                </p>
                                <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={() => signOut()}
                            className="text-destructive focus:text-destructive"
                        >
                            <LogOut className="mr-2 h-4 w-4" />
                            Log out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    )
}
